import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';

const AdministracionCanchas = () => {
  const [canchas, setCanchas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCancha, setEditingCancha] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // Cargar canchas
  const loadCanchas = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/canchas');
      if (response.ok) {
        const data = await response.json();
        setCanchas(data);
      } else {
        message.error('Error al cargar las canchas');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las canchas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCanchas();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Abrir modal para crear nueva cancha
  const handleCreate = () => {
    setEditingCancha(null);
    form.resetFields();
    form.setFieldsValue({ activo: true });
    setModalVisible(true);
  };

  // Abrir modal para editar cancha
  const handleEdit = (record) => {
    setEditingCancha(record);
    form.setFieldsValue({
      descripcion: record.can_descripcion,
      activo: record.can_activo,
    });
    setModalVisible(true);
  };

  // Guardar cancha (crear o actualizar)
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingCancha) {
        // Actualizar
        const response = await apiClient.put(`/canchas/${editingCancha.can_id}`, values);
        if (response.ok) {
          message.success('Cancha actualizada correctamente');
          setModalVisible(false);
          loadCanchas();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar la cancha');
        }
      } else {
        // Crear
        const response = await apiClient.post('/canchas', values);
        if (response.ok) {
          message.success('Cancha creada correctamente');
          setModalVisible(false);
          loadCanchas();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear la cancha');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        // Errores de validación del formulario
        return;
      }
      message.error('Error al guardar la cancha');
    }
  };

  // Eliminar cancha
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/canchas/${id}`);
      if (response.ok) {
        message.success('Cancha eliminada correctamente');
        loadCanchas();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar la cancha');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la cancha');
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Descripción',
      dataIndex: 'can_descripcion',
      key: 'can_descripcion',
    },
    {
      title: 'Estado',
      dataIndex: 'can_activo',
      key: 'can_activo',
      width: 120,
      render: (activo) => (
        <Tag color={activo ? 'green' : 'red'}>
          {activo ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Fecha de Creación',
      dataIndex: 'can_fecha_creacion',
      key: 'can_fecha_creacion',
      width: 180,
      render: (fecha) => fecha ? new Date(fecha).toLocaleString('es-AR') : '-',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar esta cancha?"
            onConfirm={() => handleDelete(record.can_id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>Administración de Canchas</h1>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadCanchas}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nueva Cancha
            </Button>
          </Space>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {canchas.map(cancha => (
              <Card
                key={cancha.can_id}
                hoverable
                style={{
                  borderLeft: '4px solid #1890ff',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
                        {cancha.can_descripcion}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Fecha: {cancha.can_fecha_creacion ? new Date(cancha.can_fecha_creacion).toLocaleString('es-AR') : '-'}
                      </div>
                    </div>
                    <Tag color={cancha.can_activo ? 'green' : 'red'}>
                      {cancha.can_activo ? 'Activo' : 'Inactivo'}
                    </Tag>
                  </div>
                  <Space wrap>
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(cancha)}
                    >
                      Editar
                    </Button>
                    <Popconfirm
                      title="¿Estás seguro de eliminar esta cancha?"
                      onConfirm={() => handleDelete(cancha.can_id)}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                      >
                        Eliminar
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={canchas}
            loading={loading}
            rowKey="can_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} canchas`,
            }}
          />
        )}

        {/* Modal para crear/editar */}
        <Modal
          title={editingCancha ? 'Editar Cancha' : 'Nueva Cancha'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
        >
          <Form
            form={form}
            layout="vertical"
          >
            <Form.Item
              name="descripcion"
              label="Descripción"
              rules={[
                { required: true, message: 'La descripción es requerida' },
                { max: 100, message: 'La descripción no puede exceder 100 caracteres' },
              ]}
            >
              <Input placeholder="Ej: Cancha 1, Cancha 2, Cancha Central" />
            </Form.Item>

            <Form.Item
              name="activo"
              label="Estado"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default AdministracionCanchas;

